// Web-adapted API services
import { ENV } from './config.js';

// Re-export ENV for use in other components
export { ENV };

// Web storage utilities (replacing AsyncStorage)
const webStorage = {
    async getItem(key) {
        const value = localStorage.getItem(key);
        // console.log(`Getting ${key}:`, value ? 'exists' : 'not found');
        return value;
    },
    async setItem(key, value) {
        // console.log(`Setting ${key}:`, value ? 'value set' : 'empty value');
        localStorage.setItem(key, value);
    },
    async removeItem(key) {
        // console.log(`Removing ${key}`);
        localStorage.removeItem(key);
    }
};

// Storage keys
export const STORAGE_KEYS = {
    AUTH_TOKEN: 'auth_token',
    USER_ID: 'user_id',
    USER_DATA: 'user_data',
    B_PARTNER: 'b_partner',
    LAST_SELECTED_ORG_ID: 'last_selected_org_id',
};

// Debug function to check current auth state
export const debugAuthState = () => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);

    // console.log('=== AUTH DEBUG ===');
    // console.log('Token exists:', !!token);
    // console.log('Token length:', token ? token.length : 0);
    // console.log('Token preview:', token ? token.substring(0, 20) + '...' : 'none');
    // console.log('User data exists:', !!userData);
    // console.log('User ID exists:', !!userId);
    // console.log('==================');

    return { token, userData, userId };
};

// Function to clear all auth data
export const clearAuthData = () => {
    // console.log('Clearing all authentication data...');
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    localStorage.removeItem(STORAGE_KEYS.USER_ID);
    localStorage.removeItem(STORAGE_KEYS.B_PARTNER);
    // localStorage.removeItem(STORAGE_KEYS.LAST_SELECTED_ORG_ID);
    // console.log('Authentication data cleared');
};

// Global token expiration handler - will be set by the TokenExpirationProvider
let tokenExpirationHandler = null;

export const setTokenExpirationHandler = (handler) => {
    tokenExpirationHandler = handler;
};

// API response handler
export const handleResponse = async (response) => {
    if (!response.ok) {
        // Check for 401 Unauthorized (token expired)
        if (response.status === 401) {
            // Trigger token expiration handler if available
            if (tokenExpirationHandler) {
                tokenExpirationHandler();
                // Return a rejected promise to stop further processing
                throw new Error('Session expired');
            }
        }

        let errorMessage;

        // Clone the response so we can try multiple ways to read it
        const clonedResponse = response.clone();

        try {
            const errorData = await response.json();
            // console.log('Server error response:', errorData);

            // Try multiple possible error message fields
            errorMessage = errorData.error || errorData.detail || errorData.message || errorData.msg || errorData.errorMessage;

            // If no error message found in the expected fields, try to extract from the entire response
            if (!errorMessage && typeof errorData === 'string') {
                errorMessage = errorData;
            } else if (!errorMessage && errorData) {
                // Look for any field that might contain the error message
                const possibleErrorFields = Object.values(errorData).find(value =>
                    typeof value === 'string' && value.length > 0 && value !== 'error'
                );
                errorMessage = possibleErrorFields || 'An error occurred';
            }

            // console.log('Extracted error message:', errorMessage);
        } catch (e) {
            // If JSON parsing fails, try to get the response as text using the cloned response
            try {
                const textResponse = await clonedResponse.text();
                // console.log('Text error response:', textResponse);
                errorMessage = textResponse && textResponse.trim() ? textResponse.trim() : (response.statusText || 'Server error occurred');
            } catch (textError) {
                // console.log('Failed to read text response:', textError);
                errorMessage = response.statusText || 'Server error occurred';
            }
        }
        throw new Error(errorMessage);
    }
    try {
        if (response.status === 201 || response.status === 200) {
            const data = await response.json();
            if (data.data) {
                return data.data;
            }
            return data;
        }
        return await response.json();
    } catch (e) {
        console.error('Response parsing error:', e);
        // For location APIs that might return plain text
        const text = await response.text();
        // console.log('Response as text:', text);
        return text;
    }
};

// Special response handler for login that doesn't trigger token expiration
const handleLoginResponse = async (response) => {
    if (!response.ok) {
        let errorMessage;

        // Clone the response so we can try multiple ways to read it
        const clonedResponse = response.clone();

        try {
            const errorData = await response.json();
            // console.log('Server error response:', errorData);

            // Try multiple possible error message fields
            errorMessage = errorData.error || errorData.detail || errorData.message || errorData.msg || errorData.errorMessage;

            // If no error message found in the expected fields, try to extract from the entire response
            if (!errorMessage && typeof errorData === 'string') {
                errorMessage = errorData;
            } else if (!errorMessage && errorData) {
                // Look for any field that might contain the error message
                const possibleErrorFields = Object.values(errorData).find(value =>
                    typeof value === 'string' && value.length > 0 && value !== 'error'
                );
                errorMessage = possibleErrorFields || 'An error occurred';
            }

            // console.log('Extracted error message:', errorMessage);
        } catch (e) {
            // If JSON parsing fails, try to get the response as text using the cloned response
            try {
                const textResponse = await clonedResponse.text();
                // console.log('Text error response:', textResponse);
                errorMessage = textResponse && textResponse.trim() ? textResponse.trim() : (response.statusText || 'Server error occurred');
            } catch (textError) {
                // console.log('Failed to read text response:', textError);
                errorMessage = response.statusText || 'Server error occurred';
            }
        }
        throw new Error(errorMessage);
    }
    try {
        if (response.status === 201 || response.status === 200) {
            const data = await response.json();
            if (data.data) {
                return data.data;
            }
            return data;
        }
        return await response.json();
    } catch (e) {
        console.error('Response parsing error:', e);
        // For location APIs that might return plain text
        const text = await response.text();
        // console.log('Response as text:', text);
        return text;
    }
};

// Auth API functions
export const login = async (username, password) => {
    try {
        const response = await fetch(`${ENV.API_BASE_URL}/auth/tokens`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                userName: username,
                password: password,
                "parameters": { "clientId": 1000000, "roleId": 1000005 }
            }),
        });
        const data = await handleLoginResponse(response);

        if (data.token) {
            await webStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, data.token);
            await webStorage.setItem(STORAGE_KEYS.USER_ID, data.userId.toString());
            const userData = await fetchUserData(data.userId, data.token);
            if (userData) {
                await webStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
            }

            // Fetch business partner info
            const bpartner = await fetchBPartner();
            if (bpartner) {
                await webStorage.setItem(STORAGE_KEYS.B_PARTNER, JSON.stringify(bpartner));
            }

            // Determine user type
            const userTypeFlags = {
                isBDO: !!bpartner?.isbdo,
                isHospital: !!bpartner?.isHospital,
                isCollection: !!(bpartner?.IsEmployee || bpartner?.IsVolunteer),
                isPublicUser: !!(bpartner?.isdonor && !bpartner?.isbdo && !bpartner?.isHospital && !(bpartner?.IsEmployee || bpartner?.IsVolunteer))
            };

            // If user has both BDO and Hospital roles, require role selection
            if (userTypeFlags.isBDO && userTypeFlags.isHospital) {
                return {
                    requiresRoleSelection: true,
                    token: data.token,
                    userId: data.userId,
                    username: username,
                    language: data.language,
                    userData,
                    bpartner,
                };
            }

            let userType = 'publicUser';
            if (userTypeFlags.isBDO) userType = 'isBdo';
            else if (userTypeFlags.isHospital) userType = 'isHospital';
            else if (userTypeFlags.isCollection) userType = 'collection';

            // Only allow hospital and BDO
            if (!['isHospital', 'isBdo'].includes(userType)) {
                await webStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
                await webStorage.removeItem(STORAGE_KEYS.USER_ID);
                await webStorage.removeItem(STORAGE_KEYS.USER_DATA);
                await webStorage.removeItem(STORAGE_KEYS.B_PARTNER);
                throw new Error('Please login via Mobile App. Only Hospital and BDO can use the web platform.');
            }

            return {
                token: data.token,
                user: {
                    id: data.userId,
                    username: username,
                    language: data.language,
                    userType: userType,
                    ...userData
                }
            };
        }
        throw new Error('No token received from server');
    } catch (error) {
        console.error('Login error:', error);
        throw new Error(error.message || 'Login failed');
    }
};

// Complete login after role selection for dual-role users
export const loginWithRole = async (pendingLogin, selectedRole) => {
    const { token, userId, username, language, userData, bpartner } = pendingLogin;
    const userType = selectedRole === 'bdo' ? 'isBdo' : 'isHospital';
    return {
        token,
        user: {
            id: userId,
            username,
            language,
            userType,
            ...userData
        }
    };
};

export const logout = async () => {
    try {
        await webStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
        await webStorage.removeItem(STORAGE_KEYS.USER_DATA);
        await webStorage.removeItem(STORAGE_KEYS.USER_ID);
        await webStorage.removeItem(STORAGE_KEYS.B_PARTNER);
        // await webStorage.removeItem(STORAGE_KEYS.LAST_SELECTED_ORG_ID);
        return { success: true };
    } catch (error) {
        console.error('Logout error:', error);
        throw new Error('Logout failed');
    }
};

export const fetchUserData = async (userId, token) => {
    try {
        const response = await fetch(`${ENV.API_BASE_URL}/models/ad_user?$select=Name,Value,Email,C_BPartner_ID,C_BPartner_Location_ID,Position&$filter=ad_user_id eq ${userId}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        const data = await handleResponse(response);
        if (data.records && data.records.length > 0) {
            return data.records[0];
        }
        return null;
    } catch (error) {
        console.error('Error fetching user data:', error);
        throw new Error('Failed to fetch user data');
    }
};

export const fetchBPartner = async () => {
    try {
        const userDataStr = await webStorage.getItem(STORAGE_KEYS.USER_DATA);
        const userData = JSON.parse(userDataStr);
        const bpartnerId = userData.C_BPartner_ID?.id;
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);

        // Fetch business partner data
        const [bpartnerResponse, locations] = await Promise.all([
            fetch(`${ENV.API_BASE_URL}/models/c_bpartner/${bpartnerId}?$select=Name,Value,Phone,EMail,gender,birthday,C_BPartner_ID,is_donor,isHospital,isBDO,isEmployee,isVolunteer,AD_Org_ID,AD_Client_ID,IsActive`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            }),
            fetchBPartnerLocations(bpartnerId)
        ]);

        const bpartnerData = await handleResponse(bpartnerResponse);

        // Combine the business partner data with locations
        return {
            ...bpartnerData,
            locations: locations || []
        };
    } catch (error) {
        console.error('Error fetching business partner:', error);
        throw new Error('Failed to fetch business partner');
    }
};

/**
 * Fetch in/out records for the current hospital (business partner)
 * Matches the logic of the mobile app's fetchInOutData
 */
export const fetchInOutData = async () => {
    try {
        // Get auth token and business partner (hospital) ID
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        const bPartnerStr = await webStorage.getItem(STORAGE_KEYS.B_PARTNER);
        let bPartnerId = null;
        if (bPartnerStr) {
            try {
                const bPartner = JSON.parse(bPartnerStr);
                bPartnerId = bPartner.id || bPartner.C_BPartner_ID?.id;
            } catch (e) {
                console.warn('Could not parse B_PARTNER from storage:', e);
            }
        }
        // Fallback: try to get from user data
        if (!bPartnerId) {
            const userDataStr = await webStorage.getItem(STORAGE_KEYS.USER_DATA);
            if (userDataStr) {
                try {
                    const userData = JSON.parse(userDataStr);
                    bPartnerId = userData.C_BPartner_ID?.id;
                } catch (e) { }
            }
        }
        if (!token) throw new Error('Authentication token not found');
        if (!bPartnerId) throw new Error('Business partner (hospital) ID not found');

        const url = `${ENV.API_BASE_URL_CUSTOM}/shipmentinfo?C_BPartner_ID=${bPartnerId}`;
        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        // Use handleResponse to properly handle 401 errors and trigger token expiration
        const data = await handleResponse(response);

        // The API returns an array directly, not an object with records property
        const recordsArray = Array.isArray(data) ? data : (data.records || []);

        // Map to the same structure as mobile
        return recordsArray.map(record => ({
            age: record.age,
            documentno: record.documentno,
            m_inout_id: record.m_inout_id,
            m_inoutline_id: record.m_inoutline_id,
            patientname: record.patientname,
            asi: record.asi,
        }));
    } catch (error) {
        console.error('Error fetching in/out data:', error);
        throw error;
    }
};

/**
 * Fetch blood requests for the current business partner (hospital)
 * Matches the logic of the mobile app's fetchBloodRequests
 */
export const fetchBloodRequests = async () => {
    try {
        // Get auth token and business partner ID
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        const userDataStr = await webStorage.getItem(STORAGE_KEYS.USER_DATA);

        if (!token) throw new Error('Authentication token not found');
        if (!userDataStr) throw new Error('User data not found');

        const userData = JSON.parse(userDataStr);
        const bpartnerId = userData.C_BPartner_ID?.id;

        if (!bpartnerId) throw new Error('Business partner ID not found in user data');

        // Compose API URL (same as mobile)
        const response = await fetch(
            `${ENV.API_BASE_URL}/models/c_order?$select=DocumentNo,C_BPartner_ID,C_BPartner_Hospital_ID,Blood_group,x_match,DocStatus,PatientName,PatientID,rh_factor,age,Sex,MobileNo,ward,BedNo,TypeOfUrgency,DateOrdered,NeededCrossMatch,AD_Org_ID,M_Warehouse_ID,Description,Help&$filter=c_bpartner_id eq ${bpartnerId}&$orderby=DateOrdered desc,DocumentNo desc`,

            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            }
        );

        if (!response.ok) throw new Error('Failed to fetch blood requests');
        const data = await response.json();

        // Map to the same structure as mobile
        return (data.records || []).map(request => ({
            id: request.id,
            documentNo: request.DocumentNo,
            docStatus: request.DocStatus?.identifier || 'Unknown',
            docStatusId: request.DocStatus?.id || '',
            patientName: request.PatientName || 'N/A',
            bloodGroup: request.Blood_group?.identifier || 'N/A',
            rhFactor: request.rh_factor?.identifier || 'N/A',
            patientId: request.PatientID || 'N/A',
            hospital: request.C_BPartner_Hospital_ID?.identifier || 'N/A',
            ward: request.Ward || 'N/A',
            bedNo: request.BedNo || 'N/A',
            typeOfUrgency: request.TypeOfUrgency || 'N/A',
            dateOrdered: request.DateOrdered,
            age: request.age,
            x_match: request.x_match?.identifier || 'N/A',
            sex: request.Sex?.identifier || 'N/A',
            mobileNo: request.MobileNo || 'N/A',
            patientId: request.PatientID || 'N/A',
            neededCrossMatch: request.NeededCrossMatch || false,
            organization: request.AD_Org_ID?.identifier || 'N/A',
            warehouse: request.M_Warehouse_ID?.identifier || 'N/A',
            description: request.Description || '',
            Help: request.Help || '',
            created: request.Created,
            updated: request.Updated,
        }));
    } catch (error) {
        console.error('Error fetching blood requests:', error);
        throw error;
    }
};

/**
 * Fetch order lines for a specific order ID
 * Matches the logic of the mobile app's fetchOrderLines
 */
export const fetchOrderLines = async (orderId) => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) throw new Error('Authentication token not found');

        const response = await fetch(
            `${ENV.API_BASE_URL}/models/c_orderline?$filter=c_order_id eq ${orderId}`,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            }
        );

        if (!response.ok) throw new Error('Failed to fetch order lines');

        const data = await response.json();
        return data.records.map(line => ({
            id: line.id,
            productName: line.M_Product_ID?.identifier || 'N/A',
            productId: line.M_Product_ID?.id,
            quantity: line.QtyOrdered,
            uom: line.C_UOM_ID?.identifier || 'N/A',
            priceList: line.PriceList,
            priceActual: line.PriceActual,
            lineNetAmount: line.LineNetAmt,
            description: line.Description || '',
            dateOrdered: line.DateOrdered,
            warehouse: line.M_Warehouse_ID?.identifier || 'N/A',
            currency: line.C_Currency_ID?.identifier || 'NPR',
            organization: line.AD_Org_ID?.identifier || 'N/A',
            created: line.Created,
            updated: line.Updated
        }));
    } catch (error) {
        console.error('Error fetching order lines:', error);
        throw error;
    }
};

// Dashboard API functions
export const fetchTodaysCampaigns = async () => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) {
            // console.log('No authentication token available for campaigns');
            return [];
        }

        const today = new Date().toISOString().split('T')[0];
        const response = await fetch(`${ENV.API_BASE_URL}/models/c_campaign?$filter=startdate le '${today}' and enddate ge '${today}'`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        const data = await handleResponse(response);
        return data.records || [];
    } catch (error) {
        console.error('Error fetching campaigns:', error);
        return [];
    }
};

export const fetchStats = async () => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        const userDataStr = await webStorage.getItem(STORAGE_KEYS.USER_DATA);

        if (!token) {
            console.log('No authentication token available for stats');
            return {
                campsConducted: 0,
                completedDonations: 0,
                pendingBloodRequests: 0,
                completedBloodRequests: 0,
                bloodShipments: 0,
                issuedBloods: 0,
                usedBloods: 0,
                invoices: 0,
            };
        }

        let url = `${ENV.API_BASE_URL_CUSTOM}/dashboard/stats`;

        // Add C_BPartner_ID if available in user data
        if (userDataStr) {
            try {
                const userData = JSON.parse(userDataStr);
                const bpartnerId = userData.C_BPartner_ID?.id;
                if (bpartnerId) {
                    url += `?C_BPartner_ID=${bpartnerId}`;
                }
            } catch (parseError) {
                console.error('Error parsing user data:', parseError);
            }
        }

        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        const data = await handleResponse(response);
        return data;
    } catch (error) {
        console.error('Error fetching stats:', error);
        return {
            campsConducted: 0,
            completedDonations: 0,
            pendingBloodRequests: 0,
            completedBloodRequests: 0,
            bloodShipments: 0,
            issuedBloods: 0,
            usedBloods: 0,
            invoices: 0,
        };
    }
};

export const fetchNotice = async (organizationId) => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);

        if (!token) {
            console.log('No authentication token available for notices');
            return [];
        }

        const url = `${ENV.API_BASE_URL_CUSTOM}/dashboard/notice?AD_Org_ID=${organizationId}&$orderby=Created desc`;
        // console.log('Notice API URL being used:', url);

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        const data = await handleResponse(response);
        // console.log('Notice API response:', data);
        // The API returns the notices directly as an array, not wrapped in records
        return Array.isArray(data) ? data : (data.records || []);
    } catch (error) {
        console.error('Error fetching notices:', error);
        return [];
    }
};

export const fetchPublicNotice = async (organizationId) => {
    const url = `${ENV.API_BASE_URL_CUSTOM}/dashboard/notice?AD_Org_ID=${organizationId}&$orderby=Created desc`;
    console.log('Notice API URL being used:', url);

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
    });

    return handleResponse(response);
};

export const fetchUpcomingCampaign = async (organizationId) => {
    const url = `${ENV.API_BASE_URL_CUSTOM}/dashboard/campdetails?AD_Org_ID=${organizationId}&$orderby=Created desc`;
    console.log('Notice API URL being used:', url);

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
    });

    return handleResponse(response);
};

export const fetchOrganizations = async () => {
    try {
        // console.log("fetchOrganizations called");
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) throw new Error('Authentication required: No token found.');

        const url = `${ENV.API_BASE_URL}/models/ad_org?$select=Name,Value&$filter=AD_Org_ID neq 0&isActive eq 'Y' &$orderby=Name asc`;
        // console.log("Making request to:", url);

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        // Use handleResponse to properly handle 401 errors and trigger token expiration
        const data = await handleResponse(response);
        // console.log("Organizations response data:", data);
        if (data && Array.isArray(data.records)) {
            return data.records;
        } else if (Array.isArray(data)) {
            return data;
        } else {
            console.warn("API response for organizations was not in an expected array or { records: [] } format:", data);
            return [];
        }

    } catch (error) {
        console.error("Error fetching organizations:", error);
        throw error;
    }
};

export const fetchUserOrganizations = async () => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        const userDataStr = await webStorage.getItem(STORAGE_KEYS.USER_DATA);

        console.log('Token for user organizations:', token ? 'exists' : 'missing');
        console.log('User data for user organizations:', userDataStr ? 'exists' : 'missing');

        if (!token) {
            console.log('No authentication token available');
            return [];
        }

        if (!userDataStr) {
            console.log('No user data available');
            return [];
        }

        const userData = JSON.parse(userDataStr);
        const response = await fetch(`${ENV.API_BASE_URL}/models/AD_User_OrgAccess?$filter=AD_User_ID eq ${userData.id} & AD_Org_ID neq 0`, {
            headers: {
                'accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });
        const data = await handleResponse(response);
        return data.records || [];
    } catch (error) {
        console.error('Error fetching user organizations:', error);
        return [];
    }
};

export const fetchCampaigns = async () => {
    try {
        const bpartnerObject = await fetchBPartner();
        // console.log('Fetched bpartner object:', bpartnerObject);

        const bpartnerId = bpartnerObject?.id;

        if (!bpartnerId) {
            console.warn('Business Partner ID not found from fetchBPartner(). Cannot filter campaigns.');
            throw new Error('Business Partner ID is required to fetch campaigns.');
        }

        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) throw new Error('Authentication token not found');

        // Use the same filter as React Native: C_BPartner_ID eq ${bpartnerId}
        const filter = `C_BPartner_ID eq ${bpartnerId}`;
        const url = `${ENV.API_BASE_URL}/models/c_campaign?$filter=${filter}&$orderby=Created desc`;
        // console.log('Fetching campaigns from URL:', url);

        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        const data = await handleResponse(response);
        // console.log('Campaigns API response:', data);

        // Map the data to match React Native format
        return data.records.map(item => ({
            id: item.id,
            name: item.Name,
            description: item.Description,
            startDate: item.StartDate,
            phone: item.Phone,
            email: item.EMail,
            position: item.Position,
            targetQty: item.TargetQty,
            wardNo: item.ward_no,
            venue: item.Address1,
            Time: item.StartTime,

            C_Location_ID: item.C_Location_ID?.id,
            AD_Org_ID: item.AD_Org_ID?.id,
            AD_User_ID: item.AD_User_ID?.id,
            C_BPartner_Location_ID: item.C_BPartner_Location_ID?.id,
            C_Channel_ID: item.C_Channel_ID?.id,

            n_province_ID: item.n_province_ID?.id,
            n_district_ID: item.n_district_ID?.id,
            n_locgov_ID: item.n_locgov_ID?.id,

            organization: item.AD_Org_ID?.identifier,
            district: item.n_district_ID?.identifier,
            localGovernment: item.n_locgov_ID?.identifier,
            C_BPartner_ID: item.C_BPartner_ID, // Keep full object for identifier access

            IsSummary: item.IsSummary,
            Costs: item.Costs,
            StartTime: item.StartTime,
            DocStatus: item.DocStatus,
            isActive: item.IsActive,
            created: item.Created,
            updated: item.Updated,
            IsActive: item.IsActive,
            Address1: item.Address1,
            Address2: item.Address2,
            City: item.City,
            Postal: item.Postal,
            Country: item.Country,
            RegionName: item.RegionName,
            C_Country_ID: item.C_Country_ID?.id,
            C_Region_ID: item.C_Region_ID?.id,
            C_City_ID: item.C_City_ID?.id,
            C_Location_ID: item.C_Location_ID?.id,
            C_BPartner_Location_ID: item.C_BPartner_Location_ID?.id,
            AD_Org_ID: item.AD_Org_ID?.id,
            AD_User_ID: item.AD_User_ID?.id,
            C_Channel_ID: item.C_Channel_ID?.id,
            n_province_ID: item.n_province_ID?.id,
            n_district_ID: item.n_district_ID?.id,
            n_locgov_ID: item.n_locgov_ID?.id,
            StartDate: item.StartDate,
            EndDate: item.EndDate,
            TargetQty: item.TargetQty,
            Phone: item.Phone,
            EMail: item.EMail,
            Position: item.Position,
            Description: item.Description,
            ward_no: item.ward_no,
            Name: item.Name,
            IsActive: item.IsActive,
            DocStatus: item.DocStatus,
            Created: item.Created,
            Updated: item.Updated,
            Costs: item.Costs,
            StartTime: item.StartTime,
        })) || [];
    } catch (error) {
        console.error('Error fetching campaigns:', error);
        throw error;
    }
};

export const fetchCampaignsByOrganization = async (organizationId) => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);

        if (!token) {
            console.log('No authentication token available');
            return [];
        }

        console.log('Fetching campaigns for organization ID:', organizationId);
        // Use exact filter from React Native: ad_org_id eq ${organizationId} and DocStatus eq 'R'
        const filter = `ad_org_id eq ${organizationId} and DocStatus eq 'R'`;
        const url = `${ENV.API_BASE_URL}/models/C_Campaign?$filter=${filter}`;
        console.log('API URL:', url);
        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        const data = await handleResponse(response);
        console.log('API Response for campaigns:', data);
        console.log('Campaigns records:', data.records);
        return data.records || [];
    } catch (error) {
        console.error('Error fetching campaigns by organization:', error);
        return [];
    }
};

export const fetchDonationRegistrationsByCampaignId = async (campaignId) => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) {
            throw new Error('Authentication token not found. Please log in.');
        }

        console.log('Fetching donation registrations for campaign ID:', campaignId);
        const url = `${ENV.API_BASE_URL}/models/c_campaign_donation_reg?$filter=C_Campaign_ID eq ${campaignId}`;
        console.log('API URL:', url);

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            let errorMessage = `Failed to fetch donation registrations. Status: ${response.status}`;
            try {
                const errorData = await response.json();
                if (errorData.message) errorMessage = errorData.message;
                else if (errorData.error) errorMessage = errorData.error;
            } catch { }
            throw new Error(errorMessage);
        }

        const data = await response.json();
        console.log('Donation registrations response:', data);

        if (data && Array.isArray(data.records)) {
            console.log('Found', data.records.length, 'donation registrations');
            return data.records;
        } else if (Array.isArray(data)) {
            console.log('Found', data.length, 'donation registrations (direct array)');
            return data;
        } else {
            console.warn('Unexpected response format for donation registrations:', data);
            return [];
        }
    } catch (error) {
        console.error('Error fetching donation registrations:', error);
        throw error;
    }
};

export const fetchCampaignData = async (campaignId) => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) {
            throw new Error('Authentication token not found');
        }

        const queryParams = campaignId ? `/${campaignId}` : '';
        const response = await fetch(`${ENV.API_BASE_URL}/models/C_Campaign${queryParams}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        return await handleResponse(response);
    } catch (error) {
        console.error('Error fetching campaign data:', error);
        throw error;
    }
};

export const updateCampaign = async (campaignId, campaignData) => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) {
            throw new Error('Authentication token not found');
        }

        console.log('Updating campaign:', campaignId, campaignData);
        const response = await fetch(`${ENV.API_BASE_URL}/models/c_campaign/${campaignId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(campaignData),
        });
        return await handleResponse(response);
    } catch (error) {
        console.error('Error updating campaign:', error);
        throw error;
    }
};

// Additional donation registration functions
export const getCampaignDonationReg = async (id) => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) {
            throw new Error('Authentication token not found. Please log in.');
        }

        const response = await fetch(`${ENV.API_BASE_URL}/models/c_campaign_donation_reg/${id}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });
        return await handleResponse(response);
    } catch (error) {
        console.error('Error fetching donation registration:', error);
        throw error;
    }
};

export const createCampaignDonationReg = async (data) => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) {
            throw new Error('Authentication token not found. Please log in.');
        }

        const response = await fetch(`${ENV.API_BASE_URL}/models/c_campaign_donation_reg`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(data),
        });
        return await handleResponse(response);
    } catch (error) {
        console.error('Error creating donation registration:', error);
        throw error;
    }
};

export const updateCampaignDonationReg = async (id, data) => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) {
            throw new Error('Authentication token not found. Please log in.');
        }

        const response = await fetch(`${ENV.API_BASE_URL}/models/c_campaign_donation_reg/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(data),
        });
        return await handleResponse(response);
    } catch (error) {
        console.error('Error updating donation registration:', error);
        throw error;
    }
};

export const deleteCampaignDonationReg = async (id) => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) {
            throw new Error('Authentication token not found. Please log in.');
        }

        const response = await fetch(`${ENV.API_BASE_URL}/models/c_campaign_donation_reg/${id}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });
        return await handleResponse(response);
    } catch (error) {
        console.error('Error deleting donation registration:', error);
        throw error;
    }
};

// Additional functions for edit donation registration
export const fetchRegistrationById = async (registrationId) => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) {
            throw new Error('Authentication token not found. Please log in.');
        }

        const response = await fetch(`${ENV.API_BASE_URL}/models/c_campaign_donation_reg?$filter=c_campaign_donation_reg_ID eq ${registrationId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || `HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        if (data && data.records && data.records.length > 0) {
            return data.records[0];
        }
        throw new Error('Registration not found or no records returned.');
    } catch (error) {
        console.error('Error fetching registration:', error.message);
        throw error;
    }
};

export const updateRegistration = async (registrationId, updatedData) => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) {
            throw new Error('Authentication token not found. Please log in.');
        }

        console.log('Sending PUT request to:', `${ENV.API_BASE_URL}/models/c_campaign_donation_reg/${registrationId}`);
        console.log('Payload:', JSON.stringify(updatedData, null, 2));

        const response = await fetch(`${ENV.API_BASE_URL}/models/c_campaign_donation_reg/${registrationId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(updatedData),
        });

        if (!response.ok) {
            let errorDetails = {};
            try {
                errorDetails = await response.json();
            } catch (parseError) {
                console.warn('Failed to parse error response as JSON:', parseError);
                errorDetails = { message: 'No JSON response from server or invalid JSON.' };
            }
            throw new Error(errorDetails.message || `HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error updating registration:', error.message);
        throw error;
    }
};

// Data mapping functions (simplified versions for web)
export const mapApiDataToFormState = (apiData) => {
    if (!apiData) return null;

    const getNestedValue = (obj, path, defaultValue = '') => {
        const parts = path.split('.');
        let current = obj;
        for (const part of parts) {
            if (current === null || current === undefined || typeof current !== 'object') {
                return defaultValue;
            }
            current = current[part];
        }
        return current !== null && current !== undefined ? current : defaultValue;
    };

    return {
        personalDetails: {
            donorName: getNestedValue(apiData, 'C_BPartner_ID.identifier', ''),
            donorNo: getNestedValue(apiData, 'donor_no', ''),
            age: getNestedValue(apiData, 'age', ''),
            dob: getNestedValue(apiData, 'dob', ''),
            gender: getNestedValue(apiData, 'gender.identifier', ''),
            permanentAddress: getNestedValue(apiData, 'permanent_address', ''),
            phone: getNestedValue(apiData, 'phone', ''),
            officeName: getNestedValue(apiData, 'office_name', ''),
            businessAddress: getNestedValue(apiData, 'business_address', ''),
            emailAddress: getNestedValue(apiData, 'email_address', ''),
            previousDonationCount: getNestedValue(apiData, 'previous_donation_count', ''),
            previousDonationDate: getNestedValue(apiData, 'previous_donation_date', ''),
            previouslyBloodGiven: getNestedValue(apiData, 'previously_blood_given', false)
        },
        healthRelatedDetails: {
            hypertension: getNestedValue(apiData, 'hypertension', false),
            diabetes: getNestedValue(apiData, 'diabetes', false),
            kidneyDisease: getNestedValue(apiData, 'kidney_disease', false),
            surgery: getNestedValue(apiData, 'surgery', false),
            jaundice: getNestedValue(apiData, 'jaundice', false),
            epilepsy: getNestedValue(apiData, 'epilepsy', false),
            drugUse: getNestedValue(apiData, 'drug_use', false),
            typhoid: getNestedValue(apiData, 'typhoid', false),
            stds: getNestedValue(apiData, 'stds', false),
            pregnancy: getNestedValue(apiData, 'pregnancy', false),
            tb: getNestedValue(apiData, 'tb', false),
            hivAids: getNestedValue(apiData, 'hiv_aids', false),
            breastfeeding: getNestedValue(apiData, 'breastfeeding', false),
            heartDisease: getNestedValue(apiData, 'heart_disease', false),
            lungDisease: getNestedValue(apiData, 'lung_disease', false),
            lastForeignVisit: getNestedValue(apiData, 'last_foreign_visit', '')
        },
        medicinesAndOther: {
            medicines: getNestedValue(apiData, 'medicines', ''),
            tattoo: getNestedValue(apiData, 'tattoo', false),
            recentVaccinations: getNestedValue(apiData, 'recent_vaccinations', false),
            otherany: getNestedValue(apiData, 'other_any', '')
        },
        voluntaryServices: {
            willingToDonateBlood: getNestedValue(apiData, 'consent_to_blood_donation', false),
            willingToCounseling: getNestedValue(apiData, 'cosent_to_receive_counseling', false)
        },
        healthRecord: {
            weight: getNestedValue(apiData, 'weight', ''),
            bloodPressure: getNestedValue(apiData, 'blood_pressure', ''),
            hb: getNestedValue(apiData, 'hb', ''),
            respiratorySystem: getNestedValue(apiData, 'respiratory_system', ''),
            cvs: getNestedValue(apiData, 'cvs', false),
            giSystem: getNestedValue(apiData, 'gi_system', false),
            otherHealthRecord: getNestedValue(apiData, 'other_health_record', false),
            otherHealthRecordText: getNestedValue(apiData, 'other_health_record_text', ''),
            fitness: getNestedValue(apiData, 'fitness.id', '')
        },
        bloodBagDetails: {
            bagNo: getNestedValue(apiData, 'bag_no', ''),
            bloodBagType: getNestedValue(apiData, 'M_Product_ID.id', ''),
            reaction: getNestedValue(apiData, 'reaction', false),
            reactionType: getNestedValue(apiData, 'reaction_type.id', ''),
            reactionPeriod: getNestedValue(apiData, 'reaction_period.id', ''),
            reactionReason: getNestedValue(apiData, 'reaction_reason', ''),
            bloodBagStatus: getNestedValue(apiData, 'blood_bag_status.id', ''),
            cancelled: getNestedValue(apiData, 'IsCancelled', false),
            description: getNestedValue(apiData, 'description', ''),
            active: getNestedValue(apiData, 'IsActive', false)
        }
    };
};

export const mapFormStateToApiData = (formData) => {
    const { personalDetails, healthRelatedDetails, medicinesAndOther, voluntaryServices, healthRecord, bloodBagDetails } = formData;

    return {
        donor_no: personalDetails.donorNo,
        age: personalDetails.age,
        dob: personalDetails.dob,
        gender: personalDetails.gender ? { id: personalDetails.gender } : null,
        permanent_address: personalDetails.permanentAddress,
        phone: personalDetails.phone,
        office_name: personalDetails.officeName,
        business_address: personalDetails.businessAddress,
        email_address: personalDetails.emailAddress,
        previous_donation_count: personalDetails.previousDonationCount,
        previous_donation_date: personalDetails.previousDonationDate,
        previously_blood_given: personalDetails.previouslyBloodGiven,

        // Health related details
        hypertension: healthRelatedDetails.hypertension,
        diabetes: healthRelatedDetails.diabetes,
        kidney_disease: healthRelatedDetails.kidneyDisease,
        surgery: healthRelatedDetails.surgery,
        jaundice: healthRelatedDetails.jaundice,
        epilepsy: healthRelatedDetails.epilepsy,
        drug_use: healthRelatedDetails.drugUse,
        typhoid: healthRelatedDetails.typhoid,
        stds: healthRelatedDetails.stds,
        pregnancy: healthRelatedDetails.pregnancy,
        tb: healthRelatedDetails.tb,
        hiv_aids: healthRelatedDetails.hivAids,
        breastfeeding: healthRelatedDetails.breastfeeding,
        heart_disease: healthRelatedDetails.heartDisease,
        lung_disease: healthRelatedDetails.lungDisease,
        last_foreign_visit: healthRelatedDetails.lastForeignVisit,

        // Medicines and other
        medicines: medicinesAndOther.medicines,
        tattoo: medicinesAndOther.tattoo,
        recent_vaccinations: medicinesAndOther.recentVaccinations,
        other_any: medicinesAndOther.otherany,

        // Voluntary services
        consent_to_blood_donation: voluntaryServices.willingToDonateBlood,
        cosent_to_receive_counseling: voluntaryServices.willingToCounseling,

        // Health record
        weight: healthRecord.weight,
        blood_pressure: healthRecord.bloodPressure,
        hb: healthRecord.hb,
        respiratory_system: healthRecord.respiratorySystem,
        cvs: healthRecord.cvs,
        gi_system: healthRecord.giSystem,
        other_health_record: healthRecord.otherHealthRecord,
        other_health_record_text: healthRecord.otherHealthRecordText,
        fitness: healthRecord.fitness ? { id: healthRecord.fitness } : null,

        // Blood bag details
        bag_no: bloodBagDetails.bagNo,
        M_Product_ID: bloodBagDetails.bloodBagType ? { id: bloodBagDetails.bloodBagType } : null,
        reaction: bloodBagDetails.reaction,
        reaction_type: bloodBagDetails.reactionType ? { id: bloodBagDetails.reactionType } : null,
        reaction_period: bloodBagDetails.reactionPeriod ? { id: bloodBagDetails.reactionPeriod } : null,
        reaction_reason: bloodBagDetails.reactionReason,
        blood_bag_status: bloodBagDetails.bloodBagStatus ? { id: bloodBagDetails.bloodBagStatus } : null,
        IsCancelled: bloodBagDetails.cancelled,
        description: bloodBagDetails.description,
        IsActive: bloodBagDetails.active
    };
};

// Profile API functions
export const fetchUserProfile = async () => {
    try {
        // console.log("fetchUserProfile called");
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        const userId = await webStorage.getItem(STORAGE_KEYS.USER_ID);

        // console.log("Token exists:", !!token);
        // console.log("UserId:", userId);

        if (!token) {
            throw new Error('No authentication token found');
        }

        const url = `${ENV.API_BASE_URL}/models/ad_user?$filter=ad_user_id eq ${userId}`;
        // console.log("Making request to:", url);

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        // console.log("Response status:", response.status);
        const data = await handleResponse(response);
        // console.log("User profile response data:", data);

        if (data.records && data.records.length > 0) {
            const user = data.records[0];
            const result = {
                name: user.Name2 || 'N/A',
                email: user.EMail || 'N/A',
                phone: user.Phone || 'N/A',
                phone2: user.Phone2 || 'N/A',
                position: user.Position || 'N/A',
                birthday: user.Birthday || 'N/A',
                value: user.Value || 'N/A',
                comments: user.Comments || 'N/A',
                lastLogin: user.DateLastLogin || 'N/A',
                organization: user.AD_Org_ID?.identifier || 'N/A',
                businessPartner: user.C_BPartner_ID?.identifier || 'N/A',
                location: user.C_BPartner_Location_ID?.identifier || 'N/A',
                notificationType: user.NotificationType?.identifier || 'N/A',
                isActive: user.IsActive || false,
                isFullBPAccess: user.IsFullBPAccess || false,
                isSupportUser: user.IsSupportUser || false
            };
            // console.log("Mapped user profile result:", result);
            return result;
        }
        console.log("No user records found");
        return null;
    } catch (error) {
        console.error('Error fetching user profile:', error);
        throw error;
    }
};

export const fetchHealthDetails = async () => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        const userId = await webStorage.getItem(STORAGE_KEYS.USER_ID);

        if (!token) {
            throw new Error('No authentication token found');
        }

        // For now, return mock health data since the React Native version doesn't show a specific health API
        // TODO: Implement actual health details API when available
        return {
            bloodType: 'O+',
            height: '170 cm',
            weight: '70 kg',
            allergies: 'None',
            medicalConditions: 'None',
            emergencyContact: {
                name: 'Emergency Contact',
                phone: '123-456-7890',
                relationship: 'Family'
            }
        };
    } catch (error) {
        console.error('Error fetching health details:', error);
        throw error;
    }
};

export const fetchWarehousesByOrganization = async (organizationId) => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) {
            throw new Error('No authentication token found');
        }

        const response = await fetch(`${ENV.API_BASE_URL}/models/M_Warehouse?$filter=AD_Org_ID eq ${organizationId}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        const data = await handleResponse(response);
        return data.records || [];
    } catch (error) {
        console.error('Error fetching warehouses:', error);
        throw error;
    }
};

/**
 * Update shipment - mark blood bag as used
 * @param {string} age - Patient age
 * @param {string} m_inoutline_id - M_InOutline_ID
 * @param {string} patientName - Patient name
 */
export const updateShipment = async (age, m_inoutline_id, patientName) => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) {
            throw new Error('Authentication token not found');
        }

        // Use the correct boolean field name: "isUsed" (camelCase)
        const requestBody = {
            age: age,
            M_InOutline_ID: m_inoutline_id,
            PatientName: patientName,
            isUsed: true  // Correct field name confirmed
        };

        console.log('Sending updateShipment request:', requestBody);

        const response = await fetch(`${ENV.API_BASE_URL_CUSTOM}/updateshipment`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        });

        console.log('updateShipment response status:', response.status);

        if (!response.ok) {
            const clonedResponse = response.clone();
            let errorDetails;
            try {
                errorDetails = await response.json();
                console.error('updateShipment JSON error response:', errorDetails);
            } catch (jsonError) {
                try {
                    errorDetails = await clonedResponse.text();
                    console.error('updateShipment text error response:', errorDetails);
                } catch (textError) {
                    errorDetails = `HTTP ${response.status} ${response.statusText}`;
                }
            }
            throw new Error(`Failed to update shipment: ${response.status} - ${typeof errorDetails === 'string' ? errorDetails : JSON.stringify(errorDetails)}`);
        }

        // Handle successful response directly instead of using handleResponse
        // to avoid "body stream already read" error
        try {
            const data = await response.json();
            console.log('updateShipment success response:', data);
            return data;
        } catch (jsonError) {
            // If JSON parsing fails, try to get the response as text
            console.log('JSON parsing failed, trying text response');
            try {
                // Clone the response before reading it as text
                const textData = await response.text();
                console.log('updateShipment text response:', textData);
                return textData;
            } catch (textError) {
                console.error('Failed to read response as text:', textError);
                // Return a success indicator since the response was ok
                return { success: true, message: 'Shipment updated successfully' };
            }
        }
    } catch (error) {
        console.error('Error updating shipment:', error);
        throw error;
    }
};

export const fetchBillHistory = async () => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) {
            throw new Error('No authentication token found');
        }

        // Get business partner ID from user data
        const userDataStr = await webStorage.getItem(STORAGE_KEYS.USER_DATA);
        if (!userDataStr) {
            throw new Error('User data not found');
        }

        const userData = JSON.parse(userDataStr);
        const bpartnerId = userData.C_BPartner_ID?.id;

        if (!bpartnerId) {
            throw new Error('Business partner ID not found in user data');
        }

        const response = await fetch(`${ENV.API_BASE_URL}/models/C_Invoice?$filter=C_BPartner_ID eq ${bpartnerId}&$orderby=DateDoc desc&$top=10`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        return await handleResponse(response);
    } catch (error) {
        console.error('Error fetching bill history:', error);
        throw error;
    }
};

// Campaign creation API
export const createCampaign = async (campaignData) => {
    try {
        console.log("createCampaign called with data:", campaignData);
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) throw new Error('Authentication token not found');

        const url = `${ENV.API_BASE_URL_CUSTOM}/campaigncreate`;
        console.log("Making request to:", url);

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(campaignData),
        });

        console.log("Response status:", response.status);
        const result = await handleResponse(response);
        console.log("Campaign creation result:", result);
        return result;
    } catch (error) {
        console.error('Error creating campaign:', error);
        throw error;
    }
};

// Location APIs
export const fetchProvince = async (countryId = 258) => {
    try {
        // console.log("fetchProvince called with countryId:", countryId);
        const baseUrl = `${ENV.API_BASE_URL_CUSTOM}/location/provinces`;
        const postData = {
            C_Country_ID: countryId,
        };
        const headers = {
            "Content-Type": "application/json",
        };

        // console.log("Making request to:", baseUrl);
        // console.log("Request data:", postData);

        const response = await fetch(baseUrl, {
            method: "POST",
            headers,
            body: JSON.stringify(postData),
        });

        // console.log("Response status:", response.status);
        const rawData = await handleResponse(response);
        // console.log("Raw response data:", rawData);

        // Handle both string and object responses
        let data;
        if (typeof rawData === 'string') {
            data = JSON.parse(rawData);
        } else {
            data = rawData;
        }
        // console.log("Parsed data:", data);

        const result = data.map((region) => ({
            id: region.n_province_id,
            name: region.name,
        }));
        // console.log("Mapped result:", result);
        return result;
    } catch (error) {
        console.error("Error fetching provinces:", error);
        throw error;
    }
};

export const fetchDistrict = async (provinceId) => {
    try {
        const baseUrl = `${ENV.API_BASE_URL_CUSTOM}/location/districts`;
        const postData = {
            n_province_ID: provinceId,
        };
        const headers = {
            "Content-Type": "application/json",
        };

        const response = await fetch(baseUrl, {
            method: "POST",
            headers,
            body: JSON.stringify(postData),
        });
        const rawData = await handleResponse(response);

        // Handle both string and object responses
        let data;
        if (typeof rawData === 'string') {
            data = JSON.parse(rawData);
        } else {
            data = rawData;
        }

        return data.map((city) => ({
            id: city.n_district_id,
            name: city.name,
        }));
    } catch (error) {
        console.error("Error fetching districts:", error);
        throw error;
    }
};

export const fetchLocalGovernments = async (districtId) => {
    try {
        const baseUrl = `${ENV.API_BASE_URL_CUSTOM}/location/localgovs`;
        const postData = {
            n_district_ID: districtId,
        };
        const headers = {
            "Content-Type": "application/json",
        };

        const response = await fetch(baseUrl, {
            method: "POST",
            headers,
            body: JSON.stringify(postData),
        });
        const rawData = await handleResponse(response);

        // Handle both string and object responses
        let data;
        if (typeof rawData === 'string') {
            data = JSON.parse(rawData);
        } else {
            data = rawData;
        }

        return data.map((localGov) => ({
            id: localGov.n_locgov_ID,
            name: localGov.name,
        }));
    } catch (error) {
        console.error("Error fetching local governments:", error);
        throw error;
    }
};

// Organization Info API (for signup)
export const fetchOrgInfo = async () => {
    const url = `${ENV.API_BASE_URL_CUSTOM}/dashboard/orginfo?$filter= isActive eq true`;
    // console.log('Fetching Org Info from:', url);
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        // console.log('Org Info Data:', data);
        return data;
    } catch (error) {
        console.error('Error fetching org info:', error);
        throw error;
    }
};

// User Signup API (for signup)
export const createUserSignUp = async (userData) => {
    const response = await fetch(`${ENV.API_BASE_URL_CUSTOM}/user/create`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
    });
    return handleResponse(response);
};

// Forgot Password API functions
export const fetchCheckEmail = async (forgotEmail, showAlertCallback) => {
    const url = `${ENV.API_BASE_URL_CUSTOM}/forgetpassword/checkemail?email=${forgotEmail}`;
    console.log('Notice API URL being used:', url);

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        });

        const data = await response.json();

        if (response.ok) {
            if (data.success || data.message === 'OTP sent successfully') {
                showAlertCallback('Success', 'OTP sent successfully');
            } else {
                showAlertCallback('Notice', 'Unexpected response from server');
            }
        } else {
            if (response.status === 404 || data.error === 'Email not registered') {
                showAlertCallback('Error', 'Email not registered');
            } else {
                showAlertCallback('Error', data.error || 'Something went wrong');
            }
        }
        return data;
    } catch (error) {
        showAlertCallback('Error', 'Network or server error');
        console.error('fetchCheckEmail error:', error);
        throw error;
    }
};

export const fetchOTP = async (forgotEmail, fullOtp, showAlertCallback) => {
    const url = `${ENV.API_BASE_URL_CUSTOM}/forgetpassword/verifyotp?email=${forgotEmail}&otp=${fullOtp}`;

    console.log('Verifying OTP from URL:', url);

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        });

        const data = await response.json();
        console.log('OTP verification response:', { status: response.status, data });

        if (response.ok) {
            if (data.success || data.message === 'OTP verified successfully') {
                showAlertCallback('Success', 'OTP verified successfully');
            } else {
                showAlertCallback('Notice', 'Unexpected response from server');
            }
        } else {
            // Display the actual server error message
            const errorMessage = data.error || data.message || 'Something went wrong';
            showAlertCallback('Error', errorMessage);
        }
        return data;
    } catch (error) {
        // Only show network error for actual network issues, not HTTP error responses
        if (error.name === 'TypeError' || error.message.includes('fetch')) {
            showAlertCallback('Error', 'Network or server error');
        } else {
            showAlertCallback('Error', error.message || 'An unexpected error occurred');
        }
        console.error('fetchOTP error:', error);
        throw error;
    }
};

export const fetchChangePassword = async (email, newPassword, confirmPassword, showAlertCallback) => {
    const url = `${ENV.API_BASE_URL_CUSTOM}/forgetpassword/changepassword`;

    console.log('Posting New Password to URL:', url);

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                email: email,
                newPassword: newPassword,
                confirmPassword: confirmPassword,
            }),
        });

        const data = await response.json();

        if (response.ok) {
            if (data.success || data.message === 'Password updated successfully') {
                showAlertCallback('Success', 'Password updated successfully');
            } else {
                showAlertCallback('Notice', 'Unexpected response from server');
            }
            return data;
        } else {
            if (response.status === 404 || data.error === 'Error changing password' || data.message === 'Error changing password') {
                showAlertCallback('Error', 'Error changing password');
            } else {
                showAlertCallback('Error', data.error || data.message || 'Something went wrong');
            }
            return data;
        }
    } catch (error) {
        showAlertCallback('Error', 'Network or server error');
        console.error('fetchChangePassword error:', error);
        throw error;
    }
};

// Blood Request API functions
export const createBloodRequest = async (orderData) => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) throw new Error('Authentication token not found');

        // Get business partner ID from user data
        const userDataStr = await webStorage.getItem(STORAGE_KEYS.USER_DATA);
        const userData = JSON.parse(userDataStr);
        const bpartnerId = userData.C_BPartner_ID?.id;

        if (!bpartnerId) throw new Error('Business partner ID not found');

        // Set required fields for blood request
        orderData.C_BPartner_ID = bpartnerId;
        orderData.C_DocTypeTarget_ID = 1000032;
        orderData.C_DocType_ID = 1000032;
        orderData.Bill_BPartner_ID = bpartnerId;
        orderData.Bill_Location_ID = orderData.C_BPartner_Location_ID;
        orderData.Bill_User_ID = orderData.AD_User_ID;
        orderData.M_PriceList_ID = 1000004;

        const response = await fetch(`${ENV.API_BASE_URL}/models/c_order`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(orderData),
        });

        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage = errorData.detail || 'Failed to create blood request';
            } catch (e) {
                const errorText = await response.text();
                errorMessage = 'Server error occurred while creating blood request';
            }
            throw new Error(errorMessage);
        }
        return handleResponse(response);
    } catch (error) {
        console.error('Error creating blood request:', error);
        throw error;
    }
};

export const createOrderLine = async (orderLineData) => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) throw new Error('Authentication token not found');

        const response = await fetch(`${ENV.API_BASE_URL}/models/c_orderline`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(orderLineData),
        });

        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage = errorData.detail || 'Failed to create order line';
            } catch (e) {
                const errorText = await response.text();
                errorMessage = 'Server error occurred while creating order line';
            }
            throw new Error(errorMessage);
        }
        return handleResponse(response);
    } catch (error) {
        console.error('Error creating order line:', error);
        throw error;
    }
};

export const fetchProducts = async (categoryId = null) => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) throw new Error('Authentication token not found');

        let url = `${ENV.API_BASE_URL}/models/m_product?$select=Name,Value&$filter=`;
        if (categoryId) {
            url += `M_Product_Category_ID eq ${categoryId}`;
        }

        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) throw new Error('Failed to fetch products');
        const data = await response.json();
        return data.records.map(product => ({
            id: product.id,
            name: product.Name,
            identifier: product.identifier,
            description: product.Description,
            category: product.M_Product_Category_ID?.identifier || 'N/A',
            uom: product.C_UOM_ID?.identifier || 'N/A',
            isActive: product.IsActive
        }));
    } catch (error) {
        console.error('Error fetching products:', error);
        throw error;
    }
};

export const fetchProductPrices = async (productId) => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) throw new Error('Authentication token not found');

        const response = await fetch(`${ENV.API_BASE_URL}/models/m_productprice?$filter=M_Product_ID eq ${productId}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) throw new Error('Failed to fetch product prices');
        const data = await response.json();
        return data.records.map(price => ({
            id: price.id,
            productId: price.M_Product_ID?.id,
            priceListId: price.M_PriceList_ID?.id,
            priceListName: price.M_PriceList_ID?.identifier || 'N/A',
            priceList: price.PriceList,
            priceStandard: price.PriceStandard,
            priceLimit: price.PriceLimit,
            currency: price.C_Currency_ID?.identifier || 'NPR'
        }));
    } catch (error) {
        console.error('Error fetching product prices:', error);
        throw error;
    }
};

export const fetchHospitals = async () => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) throw new Error('Authentication token not found');

        const response = await fetch(`${ENV.API_BASE_URL}/models/c_bpartner?$filter=isHospital eq true and isActive eq true &$select=Name,Value`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        const data = await handleResponse(response);
        return data.records.map(hospital => ({
            id: hospital.id,
            name: hospital.Name,
            identifier: hospital.identifier,
            isActive: hospital.IsActive,
            isHospital: hospital.isHospital
        }));
    } catch (error) {
        console.error('Error fetching hospitals:', error);
        throw error;
    }
};

export const fetchBPartnerLocations = async (bpartnerId) => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) throw new Error('Authentication token not found');

        const response = await fetch(`${ENV.API_BASE_URL}/models/c_bpartner_location?$filter=C_BPartner_ID eq ${bpartnerId}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) throw new Error('Failed to fetch business partner locations');
        const data = await response.json();
        return data.records.map(location => ({
            id: location.id,
            name: location.Name,
            address: location.Address1,
            city: location.City,
            postal: location.Postal,
            country: location.C_Country_ID?.identifier || 'N/A',
            isActive: location.IsActive
        }));
    } catch (error) {
        console.error('Error fetching business partner locations:', error);
        throw error;
    }
};

export const uploadMultipleAttachments = async (orderId, files) => {
    try {
        const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) throw new Error('Authentication token not found');

        console.log(`Starting upload of ${files.length} files for order ID: ${orderId}`);

        // Convert each file to base64 and upload individually
        const uploadPromises = files.map(async (file, index) => {
            return new Promise((resolve, reject) => {
                console.log(`Processing file ${index + 1}/${files.length}: ${file.name} (${file.size} bytes, ${file.type})`);

                const reader = new FileReader();
                reader.onload = async () => {
                    try {
                        const base64Data = reader.result.split(",")[1];
                        const uploadPayload = {
                            fileName: file.name,
                            fileType: file.type || "application/octet-stream",
                            fileSize: file.size,
                            base64Data: base64Data,
                            C_Order_ID: orderId,
                        };

                        console.log(`Uploading file: ${file.name} to ${ENV.API_BASE_URL_CUSTOM}/attachment/uploadAttachment`);
                        console.log('Upload payload structure:', {
                            fileName: uploadPayload.fileName,
                            fileType: uploadPayload.fileType,
                            fileSize: uploadPayload.fileSize,
                            C_Order_ID: uploadPayload.C_Order_ID,
                            base64DataLength: uploadPayload.base64Data ? uploadPayload.base64Data.length : 0
                        });

                        const response = await fetch(`${ENV.API_BASE_URL_CUSTOM}/attachment/uploadAttachment`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`,
                            },
                            body: JSON.stringify(uploadPayload),
                        });

                        console.log(`Upload response for ${file.name}: Status ${response.status} ${response.statusText}`);

                        if (!response.ok) {
                            let errorDetails;
                            try {
                                errorDetails = await response.json();
                                console.error(`Upload error details for ${file.name}:`, errorDetails);
                            } catch (parseError) {
                                try {
                                    errorDetails = await response.text();
                                    console.error(`Upload error text for ${file.name}:`, errorDetails);
                                } catch (textError) {
                                    errorDetails = `HTTP ${response.status} ${response.statusText}`;
                                }
                            }

                            const errorMessage = typeof errorDetails === 'object'
                                ? errorDetails.error || errorDetails.message || errorDetails.detail || JSON.stringify(errorDetails)
                                : errorDetails;

                            throw new Error(`Failed to upload ${file.name}: ${errorMessage}`);
                        }

                        const result = await response.json();
                        console.log(`Successfully uploaded ${file.name}:`, result);
                        resolve(result);
                    } catch (error) {
                        console.error(`Error processing file ${file.name}:`, error);
                        reject(error);
                    }
                };
                reader.onerror = (error) => {
                    console.error(`FileReader error for ${file.name}:`, error);
                    reject(new Error(`Failed to read file: ${file.name}`));
                };
                reader.readAsDataURL(file);
            });
        });

        // Wait for all uploads to complete
        console.log('Waiting for all uploads to complete...');
        const results = await Promise.all(uploadPromises);
        console.log(`All ${files.length} files uploaded successfully:`, results);
        return results;
    } catch (error) {
        console.error('Error uploading attachments:', error);
        throw error;
    }
};

// Function to fetch free blood organizations
export const fetchFreeBloodOrganization = async () => {
    const token = await webStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!token) {
        throw new Error('Authentication token not found. Please log in.');
    }

    try {
        // console.log('Fetching free blood organizations...');
        // console.log('API URL:', `${ENV.API_BASE_URL_CUSTOM}/relatedorg`);
        // console.log('Token available:', !!token);

        const response = await fetch(`${ENV.API_BASE_URL_CUSTOM}/relatedorg`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        // console.log('Free blood organizations response status:', response.status);
        // console.log('Response headers:', Object.fromEntries(response.headers.entries()));

        if (!response.ok) {
            console.error('Response not OK, status:', response.status);

            // Try to get error message from response
            let errorMessage = `HTTP error! Status: ${response.status}`;
            try {
                const errorData = await response.json();
                errorMessage = errorData.message || errorMessage;
            } catch (parseError) {
                // If JSON parsing fails, try to get text response for more details
                try {
                    const errorText = await response.text();
                    console.error('Non-JSON error response:', errorText.substring(0, 500));

                    // Check if it's an HTML error page
                    if (errorText.includes('<html>') || errorText.includes('<!DOCTYPE')) {
                        errorMessage = `Server returned HTML error page instead of JSON. Status: ${response.status}. Please check the API endpoint.`;
                    } else {
                        errorMessage = `Server error: ${response.status} - ${errorText.substring(0, 200)}`;
                    }
                } catch (textError) {
                    console.error('Failed to read error response as text:', textError);
                    errorMessage = `HTTP error! Status: ${response.status} - Unable to read response`;
                }
            }
            throw new Error(errorMessage);
        }

        // Try to parse JSON response
        let data;
        try {
            const responseText = await response.text();
            // console.log('Raw response text (first 500 chars):', responseText.substring(0, 500));

            // Check if response is HTML
            if (responseText.includes('<html>') || responseText.includes('<!DOCTYPE')) {
                throw new Error('Server returned HTML page instead of JSON data. The API endpoint may not exist or there may be a routing issue.');
            }

            data = JSON.parse(responseText);
        } catch (parseError) {
            console.error('Failed to parse JSON response:', parseError);
            throw new Error(`Invalid JSON response from server: ${parseError.message}`);
        }

        // console.log('Free blood organizations data received:', data);

        // Validate response structure
        if (!data || typeof data !== 'object') {
            throw new Error('Invalid response format: expected object');
        }

        // Return the data array from the response, with fallback to empty array
        const organizationsData = data.data || [];
        // console.log('Parsed organizations count:', organizationsData.length);

        if (organizationsData.length === 0) {
            console.warn('API returned empty organizations array. Possible reasons:');
            console.warn('1. No organizations are configured in the system');
            console.warn('2. Current user may not have access to view organizations');
            console.warn('3. Organizations exist but none have hospitals configured');
            console.warn('4. API endpoint may require additional parameters or filters');
        }

        return organizationsData;
    } catch (error) {
        console.error('Error fetching free blood organizations:', error);

        // Provide more specific error messages
        if (error.message.includes('Network request failed')) {
            throw new Error('Network error: Unable to connect to server. Please check your internet connection.');
        } else if (error.message.includes('JSON Parse error') || error.message.includes('Unexpected character: <')) {
            throw new Error('Server returned invalid data format. The API endpoint may be incorrect or server is experiencing issues.');
        } else {
            throw new Error(`Failed to fetch free blood organizations: ${error.message}`);
        }
    }
};